
import { useState, useEffect } from 'react';
import { UnitSystem } from '@/utils/unitConversion/types';
import { useShoppingListDates } from './useShoppingListDates';
import { useShoppingListGeneration } from './useShoppingListGeneration';
import { useShoppingListPersistence } from './useShoppingListPersistence';
import { useCustomShoppingItems } from './useCustomShoppingItems';

interface UseShoppingListOptions {
  preferredUnits: UnitSystem;
}

export const useShoppingList = (options: UseShoppingListOptions) => {
  const { preferredUnits } = options;
  
  const [adults, setAdults] = useState<number>(2);
  const [kids, setKids] = useState<number>(2);
  
  // Use the separated hooks
  const {
    sixDays,
    availableDates,
    selectedDates,
    isLoadingDates,
    mealPlansMap,
    handleDateToggle
  } = useShoppingListDates();

  const {
    shoppingList,
    isSaving,
    setShoppingList,
    saveShoppingList,
    handleItemCheckedChange
  } = useShoppingListPersistence();

  const {
    isLoadingShoppingList,
    error,
    processingStage,
    progress,
    generateShoppingList: generateList,
    setError
  } = useShoppingListGeneration({
    preferredUnits,
    onShoppingListGenerated: (list) => {
      setShoppingList(list);
      saveShoppingList(list);
    }
  });

  // Custom shopping items
  const {
    customItems,
    inactiveItems,
    addCustomItem,
    removeCustomItem,
    reactivateItem,
    toggleCustomItemChecked,
  } = useCustomShoppingItems();

  // Effect to regenerate shopping list when unit preferences change
  useEffect(() => {
    // Only regenerate if we already have a shopping list and there are selected dates
    if (shoppingList && selectedDates.length > 0) {
      // This will trigger a regeneration with the new unit preference
      generateList(selectedDates, adults, kids);
    }
  }, [preferredUnits]);

  const generateShoppingList = () => {
    // Clear existing shopping list when generation starts
    setShoppingList(null);
    generateList(selectedDates, adults, kids);
  };

  return {
    sixDays,
    availableDates,
    selectedDates,
    adults,
    kids,
    isLoadingShoppingList,
    isLoadingDates,
    shoppingList,
    error,
    processingStage,
    progress,
    mealPlansMap,
    isSaving,
    // Custom items
    customItems,
    inactiveItems,
    addCustomItem,
    removeCustomItem,
    reactivateItem,
    toggleCustomItemChecked,
    // Handlers
    setAdults,
    setKids,
    generateShoppingList,
    handleDateToggle,
    handleItemCheckedChange
  };
};
